<template>
  <div>
    <div class="container gb-text-2 text-muted" v-if="validatingToken">
      <i class="fa fa-circle-o-notch fa-spin"/> Loading...
    </div>
    <template v-if="studentUuid">
      <LibraryBanner :title="studentName" :breadcrumbs="false">
        <h1>
          <i class="fa fa-folder header-folder" />&nbsp;{{ studentName }}
          <button
            class="btn btn-success btn-copy-folder"
            @click="openCopyModal">Copy to Your Library</button>
        </h1>
        <template v-slot:buttons>
          <div class="text-muted">
            <template v-if="studentOwnerName">Shared from <b>{{ studentOwnerName }}</b>'s Library<br></template>
            <template v-if="libraryLastUpdated">Last updated {{ libraryLastUpdated | formatTimeAgo }}</template>
          </div>
        </template>
      </LibraryBanner>

      <modal
        v-if="copyEnabled"
        v-model="isCopyModalOpen"
        title="Copy to Your Library"
        size="md"
        :backdrop="false"
        :footer="false">
        <CopyStudentForm
          :focus="isCopyModalOpen"
          :student-uuid="studentUuid"
          :student-name="studentName"
          @dismiss="isCopyModalOpen = false" />
      </modal>

      <div class="container">
        <div v-if="loading">
          <p class="gb-text-2 text-muted"><i class="fa fa-circle-o-notch fa-spin"/>&nbsp;Loading resources...</p>
        </div>
        <template v-if="!loading">
          <div class="row" v-if="resources.length > 0">
            <div class="col-sm-8">
              <ResourcesLibrary :resources="displayedResources" :shared="true" />
            </div>
          </div>
          <div class="row">
            <div class="col-md-4">
              <PresentLevelsLibrary :shared="true" :presentlevels="displayedPresentlevels"/>
            </div>
            <div class="col-md-4">
              <GoalsLibrary :shared="true" :goals="displayedGoals"/>
            </div>
            <div class="col-md-4">
              <StrategyLibrary :shared="true" :strategies="displayedStrategies"/>
            </div>
          </div>
        </template>
        <SubmitFeedback type="Shared Library" />
        <TermsOfUseFooter />
      </div>
    </template>

    <ShareLinkInvalidMessage v-if="isShareTokenInvalid">
      This link is either invalid, or the folder is no longer shared by its owner. Ask the owner of the folder for a new shareable link.
    </ShareLinkInvalidMessage>

    <LimitActionModal :action-permissions="copyStudentPermissions" :show.sync="showBlocker" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { setTitle } from '@/util/title'
import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import LibraryBanner from '@/components/library/LibraryBanner'
import GoalsLibrary from '@/components/library/GoalsLibrary'
import SubmitFeedback from '@/components/SubmitFeedback'
import StrategyLibrary from '@/components/library/StrategyLibrary'
import PresentLevelsLibrary from '@/components/library/PresentLevelsLibrary'
import ResourcesLibrary from '@/components/library/ResourcesLibrary'
import CopyStudentForm from '@/components/library/CopyStudentForm'
import LimitActionModal from '@/components/LimitActionModal'
import TermsOfUseFooter from '@/components/library/TermsOfUseFooter'
import ShareLinkInvalidMessage from '@/components/library/ShareLinkInvalidMessage'

export default {
  name: 'ViewOnlyStudentLibraryPage',
  components: {
    LibraryBanner,
    GoalsLibrary,
    StrategyLibrary,
    PresentLevelsLibrary,
    ResourcesLibrary,
    CopyStudentForm,
    SubmitFeedback,
    LimitActionModal,
    ShareLinkInvalidMessage,
    TermsOfUseFooter
  },
  props: {
    shareToken: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      studentUuid: '',
      studentName: '',
      studentOwner: '',
      studentOwnerName: '',
      libraryLastUpdated: '',

      isShareTokenInvalid: false,
      validatingToken: false,

      isCopyModalOpen: false,
      showBlocker: false,

      maxItemsForContentType: {
        goals: 15,
        strategies: 15,
        presentlevels: 5,
        resources: 20
      }
    }
  },
  computed: {
    ...mapState({
      loading: state => state.library.loadingResources,
      goals: state => state.library.goals,
      strategies: state => state.library.strategies,
      presentlevels: state => state.library.presentlevels,
      resources: state => state.library.resources
    }),
    ...mapGetters('library', ['currentStudentContentCount']),
    ...mapGetters('user', ['authInfo']),

    displayedPresentlevels() {
      return this.presentlevels.slice(0, this.maxItemsForContentType.presentlevels)
    },
    displayedGoals() {
      return this.goals.slice(0, this.maxItemsForContentType.goals)
    },
    displayedStrategies() {
      return this.strategies.slice(0, this.maxItemsForContentType.strategies)
    },
    displayedResources() {
      return this.resources.slice(0, this.maxItemsForContentType.resources)
    },
    copyStudentPermissions() {
      return actionPermissions(ACTION_TYPES.COPY_STUDENT, CONTENT_TYPES.STUDENT, {}, this.authInfo)
    },
    copyEnabled() {
      return this.studentUuid && this.studentName && !this.copyStudentPermissions.block
    }
  },
  methods: {
    async fetchResources() {
      try {
        this.validatingToken = true
        let folderData = await this.$store.dispatch('library/fetchResourcesForSharedStudent', {
          shareToken: this.shareToken
        })
        this.studentUuid = folderData.collection.uuid
        this.studentName = folderData.collection.name
        this.studentOwner = folderData.collection.owner
        this.libraryLastUpdated = folderData.collection.updated_at
        if (folderData.owner && folderData.owner.name) {
          this.studentOwnerName = folderData.owner.name
        }

        this.validatingToken = false
        this.$sendEvent('Viewed Shared Student Library')
      } catch (err) {
        this.isShareTokenInvalid = true
        this.validatingToken = false
      }
    },
    openCopyModal() {
      this.$sendEvent('Clicked Copy Student Button', { access: this.copyEnabled })
      if (this.copyEnabled) {
        this.isCopyModalOpen = true
      } else {
        this.showBlocker = true
      }
    }
  },
  eventContext() {
    return {
      student_uuid: this.studentUuid,
      student_owner: this.studentOwner,
      student_content_count: this.currentStudentContentCount
    }
  },
  created() {
    this.fetchResources()
  },
  watch: {
    studentName() {
      if (this.studentName) {
        setTitle(`${this.studentName}'s Shared Library`)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.btn-copy-folder {
  vertical-align: bottom;
  margin-top: 0.3em;
  margin-left: 1em;
}
</style>"]
